import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { StyleSheet } from 'react-native';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { useNavigation, useRoute } from '@react-navigation/native';
import { globalStyles } from '../styles/globalStyles';
import { getResponsiveValue } from '../styles/breakpoints';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { setWebPlaceholderColor } from '../utils/setWebPlaceholderColor';
import { REVIEW_VRC_LABEL, REVIEW_VRC_PROMPT, REVIEW_VRC_TOPIC } from '../components/ReviewVrcButton';

const CONTACT_INPUT_IDS = ['contact-name-input', 'contact-email-input', 'contact-message-input'];

export function Contact() {
  const navigation = useNavigation();
  const route = useRoute();
  // REQ-070: the sitewide Review VRC button opens this form with ?topic=review.
  const isReviewTopic = route?.params?.topic === REVIEW_VRC_TOPIC;
  const defaultMessageType = isReviewTopic ? REVIEW_VRC_LABEL : 'General Inquiry';
  const { isDarkMode, theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    messageType: defaultMessageType,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, messageType: defaultMessageType }));
  }, [defaultMessageType]);

  const messageTypes = [
    'General Inquiry',
    REVIEW_VRC_LABEL,
    'Request Venue Addition',
    'Promotional',
    'Other'
  ];

  const themed = useMemo(() => {
    const labelColor = isDarkMode ? 'rgba(255,255,255,0.92)' : '#1a1a1a';
    const titleColor = isDarkMode ? '#ffffff' : '#1a1a1a';
    const inputBg = isDarkMode ? 'rgba(255,255,255,0.1)' : '#ffffff';
    const inputBorder = isDarkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.15)';
    const inputText = isDarkMode ? '#ffffff' : '#1a1a1a';
    const placeholder = isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)';
    const formBg = isDarkMode ? 'rgba(12,14,28,0.72)' : 'rgba(255,255,255,0.92)';
    const backBtnBg = isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.85)';
    const backBtnText = isDarkMode ? '#ffffff' : '#1a1a1a';

    return {
      backButton: { backgroundColor: backBtnBg },
      backButtonText: { color: backBtnText },
      title: { color: titleColor },
      formContainer: { backgroundColor: formBg },
      label: { color: labelColor },
      input: {
        backgroundColor: inputBg,
        borderColor: inputBorder,
        color: inputText,
      },
      select: {
        backgroundColor: inputBg,
        borderColor: inputBorder,
        color: inputText,
      },
      placeholder,
    };
  }, [isDarkMode, theme]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    CONTACT_INPUT_IDS.forEach((id) => setWebPlaceholderColor(id, themed.placeholder));
  }, [themed.placeholder]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const functions = getFunctions();
      const handleEmail = httpsCallable(functions, 'handleEmail');
      
      await handleEmail({
        template: 'contact_form',
        recipient: 'contact@venuereportcard.com',
        emailData: formData
      });

      const db = getFirestore();
      await addDoc(collection(db, 'contact_submissions'), {
        ...formData,
        createdAt: new Date().toISOString(),
        emailSent: true
      });

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        messageType: defaultMessageType,
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={globalStyles.container}>
      <BackgroundVideo />
      <View style={globalStyles.overlay}>
        <ScrollView style={styles.scrollContainer}>
          <TouchableOpacity
            style={[styles.backButton, themed.backButton]}
            onPress={() => navigation.goBack()}
          >
            <Text style={[styles.backButtonText, themed.backButtonText]}>← Back</Text>
          </TouchableOpacity>

          <Text style={[styles.title, themed.title, isReviewTopic && { marginBottom: 10 }]}>
            {isReviewTopic ? REVIEW_VRC_LABEL.toUpperCase() : 'CONTACT US'}
          </Text>
          {isReviewTopic ? (
            <Text style={[styles.reviewPrompt, themed.label]}>{REVIEW_VRC_PROMPT}.</Text>
          ) : null}
          
          <View style={[styles.formContainer, themed.formContainer]}>
            <View style={styles.inputGroup}>
              <Text style={[styles.label, themed.label]}>Name</Text>
              <TextInput
                nativeID="contact-name-input"
                style={[styles.input, themed.input]}
                value={formData.name}
                onChangeText={(text) => setFormData({ ...formData, name: text })}
                placeholder="Your name"
                placeholderTextColor={themed.placeholder}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={[styles.label, themed.label]}>Email</Text>
              <TextInput
                nativeID="contact-email-input"
                style={[styles.input, themed.input]}
                value={formData.email}
                onChangeText={(text) => setFormData({ ...formData, email: text })}
                placeholder="Your email"
                placeholderTextColor={themed.placeholder}
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={[styles.label, themed.label]}>Message Type</Text>
              {Platform.OS === 'web' ? (
                <select
                  style={{ ...styles.select, ...themed.select }}
                  value={formData.messageType}
                  onChange={(e) => setFormData({ ...formData, messageType: e.target.value })}
                >
                  {messageTypes.map((type) => (
                    <option
                      key={type}
                      value={type}
                      style={{
                        backgroundColor: isDarkMode ? '#1a1f2e' : '#ffffff',
                        color: isDarkMode ? '#ffffff' : '#1a1a1a',
                      }}
                    >
                      {type}
                    </option>
                  ))}
                </select>
              ) : null}
            </View>

            <View style={styles.inputGroup}>
              <Text style={[styles.label, themed.label]}>Message</Text>
              <TextInput
                nativeID="contact-message-input"
                style={[styles.input, styles.messageInput, themed.input]}
                value={formData.message}
                onChangeText={(text) => setFormData({ ...formData, message: text })}
                placeholder={formData.messageType === REVIEW_VRC_LABEL ? REVIEW_VRC_PROMPT : 'Your message'}
                placeholderTextColor={themed.placeholder}
                multiline
                numberOfLines={6}
                textAlignVertical="top"
              />
            </View>

            <TouchableOpacity
              style={[
                styles.submitButton,
                isSubmitting && styles.submitButtonDisabled
              ]}
              onPress={handleSubmit}
              disabled={isSubmitting}
            >
              <Text style={styles.submitButtonText}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Text>
            </TouchableOpacity>

            {submitStatus === 'success' && (
              <Text style={styles.successText}>
                Thank you for your message! We'll get back to you soon.
              </Text>
            )}
            {submitStatus === 'error' && (
              <Text style={styles.errorText}>
                There was an error sending your message. Please try again.
              </Text>
            )}
          </View>
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
    padding: 20,
  },
  backButton: {
    borderRadius: 8,
    padding: 10,
    alignItems: 'center',
    marginBottom: 20,
    alignSelf: 'flex-start',
    minWidth: 100,
  },
  backButtonText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: '600',
  },
  title: {
    fontFamily: 'Playpen Sans',
    fontSize: 48,
    marginBottom: 30,
    textAlign: 'center',
    fontWeight: '700',
  },
  reviewPrompt: {
    fontSize: 18,
    lineHeight: 26,
    textAlign: 'center',
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  formContainer: {
    borderRadius: 10,
    padding: getResponsiveValue({
      mobile: 20,
      tablet: 30,
      laptop: 40,
      desktop: 40
    }),
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
    marginBottom: 40,
    borderWidth: 1,
    borderColor: 'rgba(128,128,128,0.15)',
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    padding: 12,
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    width: '100%',
    borderWidth: 1,
  },
  messageInput: {
    minHeight: 150,
    textAlignVertical: 'top',
  },
  select: {
    borderRadius: 8,
    padding: 12,
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    width: '100%',
    borderWidth: 1,
    height: 45,
    cursor: 'pointer',
    outlineStyle: 'none',
    paddingLeft: 12,
    paddingRight: 36,
  },
  submitButton: {
    backgroundColor: '#2ecc71',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    marginTop: 20,
  },
  submitButtonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: '600',
  },
  successText: {
    color: '#2ecc71',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 20,
  },
  errorText: {
    color: '#e74c3c',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 20,
  },
});
